import React from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Offcanvas,
  Spinner,
  Table,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { AppState } from "../../store/root.reducer";
import User from "../../models/user.model";
import { ApiClient } from "../../utils/apiClient";
import { ArtworkWorkspaceSketchfabAsset, ArtworkWorkspaceViewMode } from "../../types/artworkWorkspace";
import {
  SketchfabAssetOrderDirection,
  SketchfabCollection,
  SketchfabImportJobStatus,
  SketchfabOAuthStatus,
} from "../../types/sketchfabData";
import { WorkspaceStatusChip } from "./WorkspaceStatusChip";
import workspaceTheme from "./workspaceTheme.module.css";
import { trackWorkspaceEvent } from "../../utils/workspaceTelemetry";
import { getNormalizedUserRoles, hasAdminCapability } from "../../utils/userAccess";

const BASE_API = process.env.REACT_APP_BASE_API || "";
const apiClient = ApiClient(BASE_API);
const INITIAL_LIMIT = 60;

type Props = {
  viewMode: ArtworkWorkspaceViewMode;
  onViewModeChange: (mode: ArtworkWorkspaceViewMode) => void;
};

type BrowseOptions = {
  query: string;
  collectionUid: string;
  orderDirection: SketchfabAssetOrderDirection;
};

const isTerminalJobStatus = (status?: string | null) =>
  status === "completed" || status === "completed_with_errors" || status === "failed";

const isActiveJobStatus = (status?: string | null) => status != null && !isTerminalJobStatus(status);

const formatJobStatus = (status?: string | null) => {
  switch (status) {
    case "queued":
      return "Queued";
    case "running":
      return "Importing";
    case "completed":
      return "Completed";
    case "completed_with_errors":
      return "Completed With Errors";
    case "failed":
      return "Failed";
    default:
      return "Unknown";
  }
};

const formatCreatedDate = (createdAt?: string): string | null => {
  if (!createdAt) {
    return null;
  }
  const parsed = Date.parse(createdAt);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return new Date(parsed).toISOString().slice(0, 10);
};

const formatJobTimestamp = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return new Date(parsed).toLocaleString();
};

const formatQueueScopeLabel = (kind?: "institution" | "user" | null) => {
  if (kind === "institution") {
    return "Institution queue";
  }
  if (kind === "user") {
    return "User queue";
  }
  return null;
};

const shouldRenderStatusReason = (job: SketchfabImportJobStatus | null) => {
  if (!job?.statusReason) {
    return false;
  }

  return job.statusReason.trim().toLowerCase() !== (job.status || "").trim().toLowerCase();
};

const openExternalUrl = (url?: string | null) => {
  if (!url) {
    return;
  }

  const openedWindow = window.open(url, "_blank", "noopener,noreferrer");
  if (openedWindow) {
    openedWindow.opener = null;
  }
};

const mergeStatuses = (
  items: ArtworkWorkspaceSketchfabAsset[],
  pendingSourceIds: Set<string>,
  jobStatus: string | null
) => items.map((item) => ({
  ...item,
  importStatus: pendingSourceIds.has(item.sourceId) && jobStatus && isActiveJobStatus(jobStatus)
    ? "Importing"
    : item.importStatus,
  canImport: pendingSourceIds.has(item.sourceId) && jobStatus && isActiveJobStatus(jobStatus)
    ? false
    : item.canImport,
  importDisabledReason: pendingSourceIds.has(item.sourceId) && jobStatus && isActiveJobStatus(jobStatus)
    ? "job_pending"
    : item.importDisabledReason,
}));

export const SketchfabImportWorkspaceTab = ({ viewMode, onViewModeChange }: Props) => {
  const navigate = useNavigate();
  const user = useSelector((state: AppState) => state.auth.user as User | null);
  const userRoles = getNormalizedUserRoles(user);
  const isAdminSrsUser = hasAdminCapability(user);
  const displayInstitutionName = user?.institution?.name || (isAdminSrsUser ? "Artetra" : "");

  const [oauthStatus, setOauthStatus] = React.useState<SketchfabOAuthStatus | null>(null);
  const [collections, setCollections] = React.useState<SketchfabCollection[]>([]);
  const [assets, setAssets] = React.useState<ArtworkWorkspaceSketchfabAsset[]>([]);
  const [prefetchedAssets, setPrefetchedAssets] = React.useState<ArtworkWorkspaceSketchfabAsset[]>([]);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [prefetchedCursor, setPrefetchedCursor] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [collectionUid, setCollectionUid] = React.useState("");
  const [searchDraft, setSearchDraft] = React.useState("");
  const [activeQuery, setActiveQuery] = React.useState("");
  const [orderDirection, setOrderDirection] = React.useState<SketchfabAssetOrderDirection>("desc");
  const [selectedSourceIds, setSelectedSourceIds] = React.useState<string[]>([]);
  const [activeAsset, setActiveAsset] = React.useState<ArtworkWorkspaceSketchfabAsset | null>(null);
  const [activeJob, setActiveJob] = React.useState<SketchfabImportJobStatus | null>(null);
  const [pendingSourceIds, setPendingSourceIds] = React.useState<Set<string>>(new Set());
  const browseStateRef = React.useRef<BrowseOptions>({
    query: "",
    collectionUid: "",
    orderDirection: "desc",
  });
  const refreshRequestRef = React.useRef(0);
  const activeJobRef = React.useRef<SketchfabImportJobStatus | null>(null);
  const pollSessionRef = React.useRef(0);
  const pollRequestRef = React.useRef(0);

  const activeCollection = collections.find((collection) => collection.uid === collectionUid) || null;
  const hasActiveSketchfabImport = isActiveJobStatus(activeJob?.status);
  const isAssetSelectable = React.useCallback((asset: ArtworkWorkspaceSketchfabAsset) => asset.canImport, []);

  React.useEffect(() => {
    browseStateRef.current = {
      query: activeQuery,
      collectionUid,
      orderDirection,
    };
  }, [activeQuery, collectionUid, orderDirection]);

  const resolveBrowseOptions = React.useCallback((overrides?: Partial<BrowseOptions>): BrowseOptions => ({
    query: overrides?.query ?? browseStateRef.current.query,
    collectionUid: overrides?.collectionUid ?? browseStateRef.current.collectionUid,
    orderDirection: overrides?.orderDirection ?? browseStateRef.current.orderDirection,
  }), []);
  const setTrackedActiveJob = React.useCallback((job: SketchfabImportJobStatus | null) => {
    activeJobRef.current = job;
    setActiveJob(job);
  }, []);

  const fetchAssetsPage = React.useCallback(async (cursor: string | null, options: BrowseOptions) => {
    const response = await apiClient.getArtworkWorkspaceSketchfabAssets({
      cursor: cursor || undefined,
      limit: INITIAL_LIMIT,
      search: options.query,
      collectionUid: options.collectionUid || undefined,
      orderDirection: options.orderDirection,
    });
    if (!response.status) {
      throw new Error(response.error?.message || "Failed to load Sketchfab assets");
    }

    return {
      items: response.data.assets || [],
      nextCursor: response.data.nextCursor || null,
    };
  }, []);

  const prefetchAssetsPage = React.useCallback(async (cursor: string | null, options: BrowseOptions) => {
    if (!cursor) {
      return {
        items: [] as ArtworkWorkspaceSketchfabAsset[],
        nextCursor: null as string | null,
      };
    }

    try {
      return await fetchAssetsPage(cursor, options);
    } catch (_error) {
      return {
        items: [] as ArtworkWorkspaceSketchfabAsset[],
        nextCursor: null as string | null,
      };
    }
  }, [fetchAssetsPage]);

  const refreshWorkspace = React.useCallback(async (overrides?: Partial<BrowseOptions>) => {
    const requestId = refreshRequestRef.current + 1;
    refreshRequestRef.current = requestId;
    const options = resolveBrowseOptions(overrides);
    setLoading(true);
    setError(null);
    try {
      const [oauthResponse, collectionsResponse, jobResponse] = await Promise.all([
        apiClient.getSketchfabOAuthStatus(),
        apiClient.getSketchfabCollections(),
        apiClient.getLatestSketchfabImportJob(),
      ]);

      if (refreshRequestRef.current !== requestId) {
        return;
      }

      if (oauthResponse.status) {
        setOauthStatus(oauthResponse.data);
      }
      if (collectionsResponse.status) {
        setCollections(collectionsResponse.data.collections || []);
      }
      setTrackedActiveJob(jobResponse.status ? jobResponse.data : null);

      if (oauthResponse.status && oauthResponse.data.connected) {
        const firstPage = await fetchAssetsPage(null, options);
        if (refreshRequestRef.current !== requestId) {
          return;
        }

        setAssets(firstPage.items);
        setNextCursor(firstPage.nextCursor);

        const nextPrefetch = await prefetchAssetsPage(firstPage.nextCursor, options);
        if (refreshRequestRef.current !== requestId) {
          return;
        }

        setPrefetchedAssets(nextPrefetch.items);
        setPrefetchedCursor(nextPrefetch.nextCursor);
        trackWorkspaceEvent("sketchfab_workspace_loaded", {
          collectionUid: options.collectionUid,
          query: options.query,
          orderDirection: options.orderDirection,
        });
      } else {
        setAssets([]);
        setPrefetchedAssets([]);
        setNextCursor(null);
        setPrefetchedCursor(null);
      }
    } catch (nextError: any) {
      if (refreshRequestRef.current !== requestId) {
        return;
      }

      setError(nextError?.message || "Failed to load Sketchfab workspace");
      trackWorkspaceEvent("sketchfab_workspace_failed", { message: nextError?.message || "Failed to load Sketchfab workspace" });
    } finally {
      if (refreshRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [fetchAssetsPage, prefetchAssetsPage, resolveBrowseOptions]);

  React.useEffect(() => {
    void refreshWorkspace();
  }, [refreshWorkspace]);

  React.useEffect(() => {
    if (!activeJob?.jobId || !isActiveJobStatus(activeJob.status)) {
      return;
    }

    const polledJobId = activeJob.jobId;
    const pollSession = pollSessionRef.current + 1;
    pollSessionRef.current = pollSession;

    const timer = window.setInterval(async () => {
      const refreshGeneration = refreshRequestRef.current;
      const pollRequestId = pollRequestRef.current + 1;
      pollRequestRef.current = pollRequestId;
      const response = await apiClient.getSketchfabImportJob(polledJobId);
      if (!response.status) {
        return;
      }

      if (pollSessionRef.current !== pollSession) {
        return;
      }
      if (pollRequestRef.current !== pollRequestId) {
        return;
      }
      if (refreshRequestRef.current !== refreshGeneration) {
        return;
      }
      if (!activeJobRef.current || activeJobRef.current.jobId !== polledJobId || !isActiveJobStatus(activeJobRef.current.status)) {
        return;
      }

      setTrackedActiveJob(response.data);
      if (isTerminalJobStatus(response.data.status)) {
        setPendingSourceIds(new Set());
        window.clearInterval(timer);
        void refreshWorkspace();
      }
    }, 3000);

    return () => window.clearInterval(timer);
  }, [activeJob?.jobId, activeJob?.status, refreshWorkspace, setTrackedActiveJob]);

  React.useEffect(() => {
    setSelectedSourceIds((current) => current.filter((sourceId) => assets.some((asset) => asset.sourceId === sourceId && isAssetSelectable(asset))));
  }, [assets, isAssetSelectable]);

  const onConnect = async () => {
    const response = await apiClient.getSketchfabOAuthStart();
    if (response.status && response.data.authorizeUrl) {
      trackWorkspaceEvent("sketchfab_connect_started");
      window.location.assign(response.data.authorizeUrl);
      return;
    }
    setError(response.error?.message || "Failed to start Sketchfab OAuth.");
  };

  const onDisconnect = async () => {
    const response = await apiClient.disconnectSketchfabOAuth();
    if (!response.status) {
      setError(response.error?.message || "Failed to disconnect Sketchfab.");
      return;
    }
    trackWorkspaceEvent("sketchfab_disconnected");
    refreshRequestRef.current += 1;
    setOauthStatus({ connected: false });
    setCollections([]);
    setAssets([]);
    setSelectedSourceIds([]);
    setActiveAsset(null);
    setNextCursor(null);
    setPrefetchedAssets([]);
    setPrefetchedCursor(null);
    setTrackedActiveJob(null);
  };

  const toggleSelected = (asset: ArtworkWorkspaceSketchfabAsset) => {
    if (!isAssetSelectable(asset)) {
      return;
    }

    setSelectedSourceIds((current) =>
      current.includes(asset.sourceId)
        ? current.filter((entry) => entry !== asset.sourceId)
        : [...current, asset.sourceId]
    );
  };

  const runImport = async (mode: "selected" | "all" | "collection") => {
    if (mode === "selected" && selectedSourceIds.length === 0) {
      setError("Select at least one Sketchfab asset.");
      return;
    }
    if (mode === "collection" && !collectionUid) {
      setError("Select a Sketchfab collection before importing the collection.");
      return;
    }

    const response = await apiClient.createSketchfabImportJob({
      mode,
      sourceIds: mode === "selected" ? selectedSourceIds : undefined,
      chunkSize: 25,
      collectionUid: mode === "collection" ? collectionUid : undefined,
    });
    if (!response.status) {
      const blockingJobId = (response.error?.details as any)?.blockingJobId;
      const reason = (response.error?.details as any)?.reason;
      setError(blockingJobId ? `${reason} (Job ${blockingJobId})` : (response.error?.message || "Failed to queue import."));
      trackWorkspaceEvent("sketchfab_import_queue_failed", {
        mode,
        blockingJobId,
        reason: reason || response.error?.message || "Failed to queue import.",
      });
      return;
    }

    setTrackedActiveJob(response.data);
    trackWorkspaceEvent("sketchfab_import_queued", {
      mode,
      jobId: response.data.jobId,
      status: response.data.status,
    });
    setPendingSourceIds(new Set(mode === "selected" ? selectedSourceIds : []));
    if (mode === "selected") {
      setAssets((current) => mergeStatuses(current, new Set(selectedSourceIds), response.data.status));
      setSelectedSourceIds([]);
    }
  };

  const submitSearch = async () => {
    const nextQuery = searchDraft.trim();
    setActiveQuery(nextQuery);
    setSelectedSourceIds([]);
    setNextCursor(null);
    await refreshWorkspace({
      query: nextQuery,
      collectionUid,
      orderDirection,
    });
  };

  const clearSearch = async () => {
    setSearchDraft("");
    setActiveQuery("");
    setOrderDirection("desc");
    setCollectionUid("");
    setSelectedSourceIds([]);
    await refreshWorkspace({
      query: "",
      collectionUid: "",
      orderDirection: "desc",
    });
  };

  const changeOrderDirection = async (nextDirection: SketchfabAssetOrderDirection) => {
    if (nextDirection === orderDirection) {
      return;
    }

    setOrderDirection(nextDirection);
    setSelectedSourceIds([]);
    await refreshWorkspace({
      query: activeQuery,
      collectionUid,
      orderDirection: nextDirection,
    });
  };

  const onLoadMore = async () => {
    if (loadingMore || !nextCursor) {
      return;
    }
    const requestId = refreshRequestRef.current;
    const options = resolveBrowseOptions();
    setLoadingMore(true);
    try {
      if (prefetchedAssets.length > 0) {
        if (refreshRequestRef.current !== requestId) {
          return;
        }

        const bufferedAssets = prefetchedAssets;
        const nextPrefetchCursor = prefetchedCursor;
        setAssets((current) => [...current, ...bufferedAssets]);
        setNextCursor(nextPrefetchCursor);
        setPrefetchedAssets([]);
        setPrefetchedCursor(null);

        const prefetchedPage = await prefetchAssetsPage(nextPrefetchCursor, options);
        if (refreshRequestRef.current !== requestId) {
          return;
        }

        setPrefetchedAssets(prefetchedPage.items);
        setPrefetchedCursor(prefetchedPage.nextCursor);
      } else {
        const page = await fetchAssetsPage(nextCursor, options);
        if (refreshRequestRef.current !== requestId) {
          return;
        }

        setAssets((current) => [...current, ...page.items]);
        setNextCursor(page.nextCursor);

        const prefetchedPage = await prefetchAssetsPage(page.nextCursor, options);
        if (refreshRequestRef.current !== requestId) {
          return;
        }

        setPrefetchedAssets(prefetchedPage.items);
        setPrefetchedCursor(prefetchedPage.nextCursor);
      }
    } catch (nextError: any) {
      setError(nextError?.message || "Failed to load more assets.");
    } finally {
      setLoadingMore(false);
    }
  };

  const renderedAssets = React.useMemo(
    () => mergeStatuses(assets, pendingSourceIds, activeJob?.status || null),
    [activeJob?.status, assets, pendingSourceIds]
  );

  return (
    <div className={`d-flex flex-column gap-3 ${workspaceTheme.sectionSurface}`}>
      <div className={workspaceTheme.sketchfabToolbarGrid}>
        <div className={workspaceTheme.sketchfabAccountCard}>
          <div className={workspaceTheme.sheetLabel}>Account</div>
          <div className={workspaceTheme.sheetValue}>
            {oauthStatus?.connected
              ? `Connected as ${oauthStatus.sketchfabUsername || "Sketchfab user"}`
              : "Not connected"}
          </div>
          {displayInstitutionName ? (
            <div className={workspaceTheme.secondaryText}>Institution: {displayInstitutionName}</div>
          ) : null}
          {isAdminSrsUser ? (
            <div className={workspaceTheme.secondaryText}>Import limit: Unlimited</div>
          ) : (
            <div className={workspaceTheme.secondaryText}>
              Imported via SRS: {oauthStatus?.currentImportedCount || 0} / {oauthStatus?.effectiveImportLimit || 10}
            </div>
          )}
          {!isAdminSrsUser && oauthStatus?.connected && oauthStatus?.quotaMode === "limited" ? (
            <div className={workspaceTheme.secondaryText}>
              {(oauthStatus.remainingImportSlots || 0) <= 0
                ? "Institution quota reached."
                : `${oauthStatus.remainingImportSlots || 0} import slots remaining.`}
            </div>
          ) : null}
        </div>

        <form
          className={workspaceTheme.sketchfabControlsPanel}
          onSubmit={(event) => {
            event.preventDefault();
            void submitSearch();
          }}
        >
          <div className={workspaceTheme.sketchfabFieldGrid}>
            <Form.Group className={workspaceTheme.sketchfabField}>
              <Form.Label>Collection</Form.Label>
              <Form.Select
                className={workspaceTheme.workspaceSelect}
                value={collectionUid}
                onChange={(event) => setCollectionUid(event.target.value)}
                disabled={!oauthStatus?.connected || hasActiveSketchfabImport}
              >
                <option value="">All collections</option>
                {collections.map((collection) => (
                  <option key={collection.uid} value={collection.uid}>
                    {collection.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className={`${workspaceTheme.sketchfabField} ${workspaceTheme.sketchfabSearchField}`}>
              <Form.Label>Search</Form.Label>
              <div className={workspaceTheme.sketchfabSearchRow}>
                <Form.Control
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                  placeholder="Search Sketchfab title"
                  disabled={!oauthStatus?.connected || hasActiveSketchfabImport}
                />
                <div className={workspaceTheme.sketchfabFormActions}>
                  <Button
                    variant="light"
                    type="submit"
                    className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`}
                    disabled={!oauthStatus?.connected || loading || hasActiveSketchfabImport}
                  >
                    Search
                  </Button>
                  <Button
                    variant="light"
                    type="button"
                    className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`}
                    onClick={() => void clearSearch()}
                    disabled={!oauthStatus?.connected || loading || hasActiveSketchfabImport}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </Form.Group>
          </div>

          <div className={workspaceTheme.sketchfabControlsFooter}>
              <div className={workspaceTheme.sketchfabField}>
                <Form.Label>Sort</Form.Label>
                <div className={workspaceTheme.workspaceToggleGroup} aria-label="Sort" role="group">
                  <button
                    type="button"
                    className={`${workspaceTheme.workspaceToggleButton} ${orderDirection === "desc" ? workspaceTheme.workspaceToggleButtonActive : workspaceTheme.workspaceToggleButtonInactive}`}
                    aria-pressed={orderDirection === "desc"}
                    onClick={() => void changeOrderDirection("desc")}
                    disabled={!oauthStatus?.connected || hasActiveSketchfabImport}
                  >
                    Newest
                  </button>
                  <button
                    type="button"
                    className={`${workspaceTheme.workspaceToggleButton} ${orderDirection === "asc" ? workspaceTheme.workspaceToggleButtonActive : workspaceTheme.workspaceToggleButtonInactive}`}
                    aria-pressed={orderDirection === "asc"}
                    onClick={() => void changeOrderDirection("asc")}
                    disabled={!oauthStatus?.connected || hasActiveSketchfabImport}
                  >
                    Oldest
                  </button>
                </div>
              </div>

            <div className={workspaceTheme.toolbarActions}>
              <div className={workspaceTheme.workspaceToggleGroup} role="group" aria-label="Sketchfab view mode">
                <button
                  type="button"
                  className={`${workspaceTheme.workspaceToggleButton} ${viewMode === "cards" ? workspaceTheme.workspaceToggleButtonActive : workspaceTheme.workspaceToggleButtonInactive}`}
                  aria-pressed={viewMode === "cards"}
                  onClick={() => onViewModeChange("cards")}
                >
                  Cards
                </button>
                <button
                  type="button"
                  className={`${workspaceTheme.workspaceToggleButton} ${viewMode === "list" ? workspaceTheme.workspaceToggleButtonActive : workspaceTheme.workspaceToggleButtonInactive}`}
                  aria-pressed={viewMode === "list"}
                  onClick={() => onViewModeChange("list")}
                >
                  List
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {!oauthStatus?.connected ? (
        <Alert variant="warning" className={`d-flex justify-content-between align-items-center ${workspaceTheme.surfaceAlert}`}>
          <span>Sketchfab is not connected for this account yet.</span>
          <Button variant="light" className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`} onClick={onConnect}>Connect Sketchfab</Button>
        </Alert>
      ) : null}

      {activeJob ? (
        <div className={workspaceTheme.sketchfabJobCard}>
          <div className={workspaceTheme.jobHeaderRow}>
            <div>
              <div className={workspaceTheme.sheetLabel}>Latest Import Job</div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <div className="fw-semibold">Job {activeJob.jobId}</div>
                <WorkspaceStatusChip label={formatJobStatus(activeJob.status)} />
              </div>
              {shouldRenderStatusReason(activeJob) ? (
                <div className={workspaceTheme.jobReason}>{activeJob.statusReason}</div>
              ) : null}
              <div className={workspaceTheme.jobMetaRow}>
                <span>Attempts: {activeJob.attemptCount || 0}</span>
                {activeJob.collectionName || activeJob.collectionUid ? (
                  <span>Collection: {activeJob.collectionName || activeJob.collectionUid}</span>
                ) : null}
                {formatJobTimestamp(activeJob.updatedAt || activeJob.lastHeartbeatAt || activeJob.finishedAt) ? (
                  <span>Updated: {formatJobTimestamp(activeJob.updatedAt || activeJob.lastHeartbeatAt || activeJob.finishedAt)}</span>
                ) : null}
                {formatQueueScopeLabel(activeJob.queueScopeKind) ? (
                  <span>{formatQueueScopeLabel(activeJob.queueScopeKind)}</span>
                ) : null}
              </div>
            </div>
            {hasActiveSketchfabImport ? (
              <div className={workspaceTheme.secondaryText}>Import in progress. This can continue after you leave the page.</div>
            ) : null}
          </div>
          <div className={workspaceTheme.jobStatsGrid}>
            <div className={workspaceTheme.jobStatItem}>
              <div className={workspaceTheme.jobStatLabel}>Requested</div>
              <div className={workspaceTheme.jobStatValue}>{activeJob.counters.requested}</div>
            </div>
            <div className={workspaceTheme.jobStatItem}>
              <div className={workspaceTheme.jobStatLabel}>Processed</div>
              <div className={workspaceTheme.jobStatValue}>{activeJob.counters.processed}</div>
            </div>
            <div className={workspaceTheme.jobStatItem}>
              <div className={workspaceTheme.jobStatLabel}>Imported</div>
              <div className={workspaceTheme.jobStatValue}>{activeJob.counters.imported}</div>
            </div>
            <div className={workspaceTheme.jobStatItem}>
              <div className={workspaceTheme.jobStatLabel}>Duplicates</div>
              <div className={workspaceTheme.jobStatValue}>{activeJob.counters.skippedDuplicates}</div>
            </div>
            <div className={workspaceTheme.jobStatItem}>
              <div className={workspaceTheme.jobStatLabel}>Non-downloadable</div>
              <div className={workspaceTheme.jobStatValue}>{activeJob.counters.skippedNonDownloadable}</div>
            </div>
            <div className={workspaceTheme.jobStatItem}>
              <div className={workspaceTheme.jobStatLabel}>Limit reached</div>
              <div className={workspaceTheme.jobStatValue}>{activeJob.counters.skippedLimitReached || 0}</div>
            </div>
            <div className={workspaceTheme.jobStatItem}>
              <div className={workspaceTheme.jobStatLabel}>Failed</div>
              <div className={workspaceTheme.jobStatValue}>{activeJob.counters.failed}</div>
            </div>
          </div>
          {activeJob.errorsSample?.length ? (
            <div className={`mt-3 ${workspaceTheme.secondaryText}`}>
              Sample error: {activeJob.errorsSample[0].sourceId} - {activeJob.errorsSample[0].message}
            </div>
          ) : null}
        </div>
      ) : null}

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {oauthStatus?.connected ? (
        <div className={workspaceTheme.sketchfabActionBar}>
          <Button variant="light" className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`} onClick={() => void refreshWorkspace()} disabled={loading || hasActiveSketchfabImport}>Refresh</Button>
          <Button variant="light" className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`} onClick={() => void runImport("selected")} disabled={selectedSourceIds.length === 0 || hasActiveSketchfabImport}>Import Selected</Button>
          <Button variant="light" className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`} onClick={() => void runImport("collection")} disabled={!collectionUid || hasActiveSketchfabImport}>Import Selected Collection</Button>
          <Button variant="light" className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`} onClick={() => void runImport("all")} disabled={hasActiveSketchfabImport}>Import All Downloadable</Button>
          <Button variant="light" className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`} onClick={onDisconnect} disabled={loading || hasActiveSketchfabImport}>Disconnect</Button>
        </div>
      ) : null}

      {loading ? (
        <div className="py-5 text-center"><Spinner animation="border" /></div>
      ) : viewMode === "cards" ? (
        <div className="row g-3">
          {renderedAssets.map((asset) => (
            <div className="col-md-4 col-xl-3" key={asset.sourceId}>
              <Card className={`h-100 ${workspaceTheme.darkCard}`}>
                <button type="button" className={workspaceTheme.darkCardButton} onClick={() => setActiveAsset(asset)}>
                  <Card.Img variant="top" src={asset.thumbnailUrl} style={{ height: 180, objectFit: "cover" }} />
                </button>
                <Card.Body className="d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <Form.Check
                      className={workspaceTheme.sketchfabSelectionCheck}
                      checked={selectedSourceIds.includes(asset.sourceId)}
                      onChange={() => toggleSelected(asset)}
                      disabled={!asset.canImport}
                    />
                    <WorkspaceStatusChip label={asset.importStatus} />
                  </div>
                  <div className={workspaceTheme.cardTitle}>{asset.title}</div>
                  <div className={workspaceTheme.neutralText}>{asset.creator}</div>
                  <div className={workspaceTheme.secondaryText}>{asset.license || "No license"}</div>
                  {formatCreatedDate(asset.createdAt) ? (
                    <div className={workspaceTheme.secondaryText}>Created: {formatCreatedDate(asset.createdAt)}</div>
                  ) : null}
                  <div className={`${workspaceTheme.actionPair} mt-auto pt-2`}>
                    <Button variant="light" size="sm" className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`} onClick={() => openExternalUrl(asset.sourceUrl)}>Open Source</Button>
                    {asset.linkedArtwork ? (
                      <Button variant="light" size="sm" className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`} onClick={() => navigate(`/artwork/list/${asset.linkedArtwork?.artworkId}`)}>
                        Open Linked Artwork
                      </Button>
                    ) : (
                      <Button variant="light" size="sm" className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`} disabled>
                        Not Linked
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
      ) : (
        <Table responsive hover variant="dark" className={workspaceTheme.darkTable}>
          <thead>
            <tr>
              <th></th>
              <th>Artwork</th>
              <th>Creator</th>
              <th>Created</th>
              <th>Status</th>
              <th>Linked artwork</th>
            </tr>
          </thead>
          <tbody>
            {renderedAssets.map((asset) => (
              <tr key={asset.sourceId}>
                <td>
                  <Form.Check
                    className={workspaceTheme.sketchfabSelectionCheck}
                    checked={selectedSourceIds.includes(asset.sourceId)}
                    onChange={() => toggleSelected(asset)}
                    disabled={!asset.canImport}
                  />
                </td>
                <td>
                  <button type="button" className={`btn btn-link p-0 text-decoration-none ${workspaceTheme.tableAction}`} onClick={() => setActiveAsset(asset)}>
                    {asset.title}
                  </button>
                </td>
                <td>{asset.creator}</td>
                <td>{formatCreatedDate(asset.createdAt) || "-"}</td>
                <td><WorkspaceStatusChip label={asset.importStatus} /></td>
                <td>
                  {asset.linkedArtwork ? (
                    <Button variant="light" size="sm" className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary} ${workspaceTheme.workspaceButtonSmall}`} onClick={() => navigate(`/artwork/list/${asset.linkedArtwork?.artworkId}`)}>
                      Open Linked Artwork
                    </Button>
                  ) : (
                    <span className={workspaceTheme.secondaryText}>Not linked</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <div>
        <Button variant="light" className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`} onClick={onLoadMore} disabled={!nextCursor || loadingMore}>
          {loadingMore ? "Loading..." : nextCursor ? "Load More" : "No More Assets"}
        </Button>
      </div>

      <Offcanvas className={workspaceTheme.darkOffcanvas} show={Boolean(activeAsset)} placement="end" onHide={() => setActiveAsset(null)}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{activeAsset?.title || "Sketchfab asset"}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {activeAsset ? (
            <div className={workspaceTheme.sheetSection}>
              <img src={activeAsset.thumbnailUrl} alt={activeAsset.title} className="img-fluid rounded" />
              <div className={workspaceTheme.chipRow}>
                <WorkspaceStatusChip label={activeAsset.importStatus} />
                {!activeAsset.importabilityFlags.downloadable ? (
                  <WorkspaceStatusChip label="Non-downloadable" tone="warning" />
                ) : null}
              </div>
              <div className={workspaceTheme.sheetBlock}>
                <div className={workspaceTheme.sheetLabel}>Creator</div>
                <div className={workspaceTheme.sheetValue}>{activeAsset.creator}</div>
              </div>
              <div className={workspaceTheme.sheetBlock}>
                <div className={workspaceTheme.sheetLabel}>License</div>
                <div className={workspaceTheme.sheetValue}>{activeAsset.license || "Unknown"}</div>
              </div>
              {formatCreatedDate(activeAsset.createdAt) ? (
                <div className={workspaceTheme.sheetBlock}>
                  <div className={workspaceTheme.sheetLabel}>Created</div>
                  <div className={workspaceTheme.sheetValue}>{formatCreatedDate(activeAsset.createdAt)}</div>
                </div>
              ) : null}
              <div className={workspaceTheme.actionPair}>
                <Button variant="light" className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`} onClick={() => openExternalUrl(activeAsset.sourceUrl)}>Open Source</Button>
                {activeAsset.linkedArtwork ? (
                  <Button variant="light" className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonPrimary}`} onClick={() => navigate(`/artwork/list/${activeAsset.linkedArtwork?.artworkId}`)}>
                    Open Linked Artwork
                  </Button>
                ) : (
                  <Button variant="light" className={`${workspaceTheme.workspaceButton} ${workspaceTheme.workspaceButtonSecondary}`} disabled>
                    Not Linked
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};
